import { z } from 'zod';

const exemptionSchema = z.object({
    endsAt: z.number(),
});

type Exemption = z.infer<typeof exemptionSchema>;

const STORAGE_KEY = 'nesso:trial-session-grace';
// The absolute session ceiling (HARD_SESSION_TTL). Presence alone must never mean "valid" — an
// exemption left behind by a session that ended without a sign-out has to expire on its own.
const EXEMPTION_TTL_MS = 12 * 60 * 60 * 1000;

function readExemption(): Exemption | null {
    if (typeof window === 'undefined') return null;
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const result = exemptionSchema.safeParse(JSON.parse(raw));
        return result.success ? result.data : null;
    } catch {
        console.warn("[Email Verification Gate Exemption] can't read item from sessionStorage");
        return null;
    }
}

export function hasEmailVerificationGateExemption(): boolean {
    const exemption = readExemption();

    return !!exemption && exemption.endsAt > Date.now();
}

export function grantEmailVerificationGateExemption(): void {
    if (typeof window === 'undefined') return;
    if (hasEmailVerificationGateExemption()) return;

    try {
        window.sessionStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ endsAt: Date.now() + EXEMPTION_TTL_MS }),
        );
    } catch {
        console.warn("[Email Verification Gate Exemption] can't write item to sessionStorage");
    }
}

export function clearEmailVerificationGateExemption(): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        console.warn("[Email Verification Gate Exemption] can't remove item from sessionStorage");
    }
}
